import React, { useState, useMemo, memo, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { Grid, Icon, Message, Button, Checkbox, Label, Dropdown, Header, Popup } from 'semantic-ui-react';
import { StatusLabels, StatusFilter } from '../../../components/statusTools/StatusIndicators';
import { PRIORITY_OPTIONS, STATUS_OPTIONS } from '../../../constants/statusConfig';
import { useCharacterManagement } from '../../hooks/core/useZoneHooks';
import { createOpportunity, updateOpportunity } from '../../../slices/opportunityDataSlice';
import '../../styles/bookWorksheet.css';

/**
 * CharactersListEnh component - Enhanced version with opportunity integration
 * Displays a list of characters for a selected book with opportunity management features
 * @param {Object} props - Component props
 * @param {Function} props.onCharacterSelect - Callback when a character is selected
 * @param {Function} props.onDeleteCharacter - Callback when delete button is clicked
 * @param {boolean} props.showImportCheckboxes - Whether to show import checkboxes for bulk operations
 * @param {Function} props.onSelectedCharactersChange - Callback when selected characters change
 * @param {boolean} props.showOpportunityStatus - Whether to show opportunity status indicators
 * @param {Array} props.opportunityData - Array of opportunity data for characters
 * @param {boolean} props.enableBulkActions - Whether to enable bulk action buttons
 * @param {Object} props.selectedBook - Currently selected book object
 * @returns {JSX.Element} CharactersListEnh component
 */
const CharactersListEnh = memo(({
  onCharacterSelect,
  onDeleteCharacter,
  showImportCheckboxes = false,
  onSelectedCharactersChange,
  showOpportunityStatus = false,
  opportunityData = [],
  enableBulkActions = false,
  selectedBook
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const characterManagement = useCharacterManagement();

  // Extract data from new hook
  const {
    characters,
    loading,
    error,
    selections,
    actions
  } = characterManagement;

  // Map to old interface for compatibility
  const charactersLoading = loading ? 'loading' : 'idle';
  const charactersError = error;
  const characterId = selections.characterId;
  const bookId = selections.bookId;
  const fetchCharacters = actions.fetchCharacters;
  const setCharacterSelection = actions.selectCharacter;

  // Local state for filtering
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [opportunityStatusFilter, setOpportunityStatusFilter] = useState('all');

  // State for import checkboxes (when enabled)
  const [selectedCharacters, setSelectedCharacters] = useState(new Set());
  const [importAllChecked, setImportAllChecked] = useState(false);

  // State for loading states
  const [loadingStates, setLoadingStates] = useState({});

  // Enhanced priority options with "All" option
  const enhancedPriorityOptions = useMemo(() => [
    ...PRIORITY_OPTIONS
  ], []);

  // Enhanced status options with "All" option
  const enhancedStatusOptions = useMemo(() => [
    ...STATUS_OPTIONS
  ], []);

  // Opportunity status filter options
  const opportunityStatusOptions = [
    { key: 'all', text: 'All Opportunity Statuses', value: 'all' },
    { key: 'no-opportunities', text: 'No Opportunities', value: 'no-opportunities' },
    { key: 'open', text: 'Has Open Opportunities', value: 'Open' },
    { key: 'assigned', text: 'Has Assigned Opportunities', value: 'Assigned' },
    { key: 'in-progress', text: 'Has In Progress Opportunities', value: 'In Progress' },
    { key: 'completed', text: 'Has Completed Opportunities', value: 'Completed' },
    { key: 'cancelled', text: 'Has Cancelled Opportunities', value: 'Cancelled' }
  ];

  // Fetch characters when bookId changes
  useEffect(() => {
    if (bookId) {
      fetchCharacters(bookId);
    }
  }, [fetchCharacters, bookId]);

  // Handle character selection
  const handleCharacterSelect = useCallback((character) => {
    if (onCharacterSelect) {
      onCharacterSelect(character);
    } else {
      setCharacterSelection(character.uuid);
    }
  }, [setCharacterSelection, onCharacterSelect]);

  // Handle delete button click
  const handleDeleteClick = useCallback((e, character) => {
    e.stopPropagation();
    if (onDeleteCharacter) {
      onDeleteCharacter(character);
    } else {
      if (window.confirm(`Are you sure you want to delete character "${character.name}"?`)) {
        console.log('Delete character:', character.uuid);
        // TODO: Implement character deletion
      }
    }
  }, [onDeleteCharacter]);

  // Get opportunity data for a character (moved up to be available for filtering)
  const getCharacterOpportunityData = useCallback((characterUuid) => {
    return opportunityData.find(data => data.character?.uuid === characterUuid) || {
      character: null,
      opportunities: []
    };
  }, [opportunityData]);

  // Apply filters first (moved up to be available for callbacks)
  const filteredCharacters = useMemo(() => {
    if (!characters || characters.length === 0) {
      return [];
    }

    let filtered = [...characters];

    // Apply priority filter
    if (priorityFilter !== 'all') {
      filtered = filtered.filter(character =>
        character.priority?.toLowerCase() === priorityFilter.toLowerCase());
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(character =>
        character.status?.toLowerCase() === statusFilter.toLowerCase());
    }

    // Apply opportunity status filter
    if (opportunityStatusFilter !== 'all') {
      filtered = filtered.filter(character => {
        const oppData = getCharacterOpportunityData(character.uuid);
        const opportunities = oppData.opportunities || [];

        if (opportunityStatusFilter === 'no-opportunities') {
          return opportunities.length === 0;
        }

        return opportunities.some(opp => opp.status === opportunityStatusFilter);
      });
    }

    return filtered;
  }, [characters, priorityFilter, statusFilter, opportunityStatusFilter, getCharacterOpportunityData]);

  // Handle import checkbox changes
  const handleCharacterImportChange = useCallback((characterUuid, checked) => {
    const newSelected = new Set(selectedCharacters);
    if (checked) {
      newSelected.add(characterUuid);
    } else {
      newSelected.delete(characterUuid);
    }
    setSelectedCharacters(newSelected);

    // Update import all checkbox state
    const totalCharacters = filteredCharacters.length;
    setImportAllChecked(newSelected.size === totalCharacters);

    // Notify parent component
    if (onSelectedCharactersChange) {
      onSelectedCharactersChange(Array.from(newSelected));
    }
  }, [selectedCharacters, filteredCharacters, onSelectedCharactersChange]);

  // Handle import all checkbox change
  const handleImportAllChange = useCallback((checked) => {
    const newSelected = new Set();
    if (checked) {
      filteredCharacters.forEach(character => {
        newSelected.add(character.uuid);
      });
    }
    setSelectedCharacters(newSelected);
    setImportAllChecked(checked);

    // Notify parent component
    if (onSelectedCharactersChange) {
      onSelectedCharactersChange(Array.from(newSelected));
    }
  }, [filteredCharacters, onSelectedCharactersChange]);

  // Calculate import all checkbox state (indeterminate when some but not all selected)
  const importAllIndeterminate = useMemo(() => {
    const selectedCount = selectedCharacters.size;
    const totalCount = filteredCharacters.length;
    return selectedCount > 0 && selectedCount < totalCount;
  }, [selectedCharacters.size, filteredCharacters.length]);

  // Handle creating a new opportunity
  const handleCreateOpportunity = useCallback(async (character) => {
    const loadingKey = `create-${character.uuid}`;
    setLoadingStates(prev => ({ ...prev, [loadingKey]: true }));

    try {
      const opportunityData = {
        title: `Voice Acting - ${character.name}`,
        description: `Voice acting opportunity for the character "${character.name}" in the book "${selectedBook?.name}".`,
        bookId: selectedBook?.uuid || bookId,
        characterId: character.uuid,
        compensation: '',
        compensationType: 'one-time',
        deadline: '',
        requirements: [],
        status: 'Open'
      };

      await dispatch(createOpportunity(opportunityData)).unwrap();
      // Refresh opportunities would typically happen here
    } catch (error) {
      console.error('Error creating opportunity:', error);
    } finally {
      setLoadingStates(prev => ({ ...prev, [loadingKey]: false }));
    }
  }, [dispatch, selectedBook, bookId]);

  // Handle bulk opportunity creation
  const handleBulkCreateOpportunities = useCallback(async () => {
    const selectedCharactersList = filteredCharacters.filter(char =>
      selectedCharacters.has(char.uuid)
    );

    const loadingKey = 'bulk-create';
    setLoadingStates(prev => ({ ...prev, [loadingKey]: true }));

    try {
      const promises = selectedCharactersList.map(character =>
        dispatch(createOpportunity({
          title: `Voice Acting - ${character.name}`,
          description: `Voice acting opportunity for the character "${character.name}" in the book "${selectedBook?.name}".`,
          bookId: selectedBook?.uuid || bookId,
          characterId: character.uuid,
          compensation: '',
          compensationType: 'one-time',
          deadline: '',
          requirements: [],
          status: 'Open'
        })).unwrap()
      );

      await Promise.all(promises);

      // Clear selections after successful creation
      setSelectedCharacters(new Set());
      setImportAllChecked(false);

      if (onSelectedCharactersChange) {
        onSelectedCharactersChange([]);
      }
    } catch (error) {
      console.error('Error creating bulk opportunities:', error);
    } finally {
      setLoadingStates(prev => ({ ...prev, [loadingKey]: false }));
    }
  }, [dispatch, selectedCharacters, filteredCharacters, selectedBook, bookId, onSelectedCharactersChange]);

  // Get character opportunity status summary
  const getOpportunityStatusSummary = useCallback((opportunities) => {
    if (opportunities.length === 0) return { text: 'No Opportunities', color: 'grey', count: 0 };

    const statusCounts = opportunities.reduce((acc, opp) => {
      acc[opp.status] = (acc[opp.status] || 0) + 1;
      return acc;
    }, {});

    const total = opportunities.length;
    const completed = statusCounts.Completed || 0;
    const assigned = statusCounts.Assigned || 0;
    const open = statusCounts.Open || 0;

    if (completed === total) return { text: 'All Complete', color: 'green', count: total };
    if (assigned > 0) return { text: `${assigned}/${total} Assigned`, color: 'blue', count: total };
    if (open > 0) return { text: `${open} Open`, color: 'orange', count: total };
    return { text: 'In Progress', color: 'yellow', count: total };
  }, []);

  // Status management helper functions
  const getPriorityColor = (priority) => {
    if (!priority) return 'grey';

    const priorityOption = PRIORITY_OPTIONS.find(option =>
      option.value.toLowerCase() === priority.toLowerCase());

    if (priorityOption && priorityOption.color) {
      return priorityOption.color;
    }

    switch (priority.toLowerCase()) {
      case 'high': return 'red';
      case 'medium': return 'orange';
      case 'low': return 'green';
      default: return 'grey';
    }
  };

  const getStatusColor = (status) => {
    if (!status) return 'grey';

    const statusOption = STATUS_OPTIONS.find(option =>
      option.value.toLowerCase() === status.toLowerCase());

    if (statusOption && statusOption.color) {
      return statusOption.color;
    }

    switch (status.toLowerCase()) {
      case 'new': return 'blue';
      case 'in progress': return 'yellow';
      case 'review': return 'purple';
      case 'complete': return 'green';
      case 'active': return 'blue';
      case 'draft': return 'grey';
      case 'archived': return 'black';
      case 'deleted': return 'red';
      default: return 'grey';
    }
  };


  // Show error state
  if (charactersLoading === 'failed') {
    return (
      <Message negative>
        <Message.Header>Error Loading Characters</Message.Header>
        <p>{charactersError || 'An unknown error occurred'}</p>
      </Message>
    );
  }

  // Show empty state or prompt to select a book
  if (!bookId) {
    return (
      <Message info>
        <Message.Header>No Book Selected</Message.Header>
        <p>Please select a book to view its characters.</p>
      </Message>
    );
  }

  // Show empty state for no characters
  if (!characters || characters.length === 0) {
    return (
      <Message info>
        <Message.Header>No Characters Found</Message.Header>
        <p>There are no characters in this book. Click "New Character" to create one.</p>
      </Message>
    );
  }

  // Convert BookWorksheet status/priority to BookCreator format for display
  const mapStatusForDisplay = (status) => {
    const isBookCreatorStatus = STATUS_OPTIONS.some(option =>
      option.value.toLowerCase() === status?.toLowerCase());

    if (isBookCreatorStatus) {
      return status;
    }

    const statusMap = {
      'active': 'In Progress',
      'draft': 'New',
      'archived': 'Complete',
      'deleted': 'Archived'
    };

    return statusMap[status?.toLowerCase()] || 'New';
  };

  const mapPriorityForDisplay = (priority) => {
    if (!priority) return 'Medium';
    return priority.charAt(0).toUpperCase() + priority.slice(1).toLowerCase();
  };

  // Render the characters list
  return (
    <div >
      {/* Enhanced Filter controls */}
      <div style={{ marginBottom: '1em' }}>
        <StatusFilter
          priorityFilter={priorityFilter}
          setPriorityFilter={setPriorityFilter}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          priorityOptions={enhancedPriorityOptions}
          statusOptions={enhancedStatusOptions}
        />

        {/* Opportunity Status Filter */}
        {showOpportunityStatus && (
          <div style={{ marginTop: '0.5em', zIndex: "0 !important" }}>
            <Dropdown
              selection
              placeholder="Opportunity Status Filter"
              options={opportunityStatusOptions}
              value={opportunityStatusFilter}
              onChange={(e, { value }) => setOpportunityStatusFilter(value)}
              style={{ minWidth: '200px' }}
            />

            {opportunityStatusFilter !== 'all' && (
              <Button
                size="small"
                style={{ marginLeft: '0.5em' }}
                onClick={() => setOpportunityStatusFilter('all')}
              >
                Clear Opportunity Filter
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Bulk Actions and Results Grid */}
      <Grid style={{
        marginBottom: '1em',
        marginTop: '1em',
        padding: '0.5em',
        backgroundColor: '#f8f9fa', //f8f9fa
        borderRadius: '4px',
        maxHeight: '32px',
        padding: '0px'
      }}>
        <Grid.Row verticalAlign="middle" style={{ paddingTop: '0px', height: '32px' }}>
          {/* Column 1: Select All Checkbox (when showImportCheckboxes is true) */}
          <Grid.Column width={5} style={{ paddingTop: '0px' }}>
            {showImportCheckboxes && (
              <div>
                <Checkbox
                  label="Select All"
                  checked={importAllChecked}
                  indeterminate={importAllIndeterminate}
                  onChange={(e, { checked }) => handleImportAllChange(checked)}
                />
                {selectedCharacters.size > 0 && (
                  <span style={{ marginLeft: '1em', color: '#666' }}>
                    {selectedCharacters.size} of {filteredCharacters.length} selected
                  </span>
                )}
              </div>
            )}
          </Grid.Column>

          {/* Column 2: Bulk Action Button (when conditions are met) */}
          <Grid.Column width={6}>
            {enableBulkActions && selectedCharacters.size > 0 && (
              <Button
                primary
                size="small"
                icon="plus"
                content={`Create Opportunities (${selectedCharacters.size})`}
                onClick={handleBulkCreateOpportunities}
                loading={loadingStates['bulk-create']}
              />
            )}
          </Grid.Column>

          {/* Column 3: Results Counter (always shown) */}
          <Grid.Column width={4} textAlign="right" style={{ paddingLeft: '0px', paddingRight: '0px' }}>
            <span style={{ fontSize: '0.9em', color: '#666' }}>
              Showing {filteredCharacters.length} of {characters.length} characters
            </span>
          </Grid.Column>
        </Grid.Row>
      </Grid>

      {/* Character list */}
      <Grid className='bw-character-grid-container'>
        <Grid.Row className='bw-character-grid-container-row' style={{ fontWeight: 'bold', borderBottom: '1px solid #ddd', padding: '10px 0' }}>
          <Grid.Column width={1}></Grid.Column>
          <Grid.Column width={8}>
            Name
          </Grid.Column>
          {showOpportunityStatus && (
            <Grid.Column width={showImportCheckboxes ? 4 : 4}>
              Opportunities
            </Grid.Column>
          )}
          <Grid.Column width={showImportCheckboxes ? 3 : 3}>
            Actions
          </Grid.Column>
        </Grid.Row>
        {filteredCharacters.length === 0 ? (
          <Grid.Row style={{ padding: '20px 0', textAlign: 'center' }}>
            <Grid.Column width={16}>
              <p style={{ color: '#888' }}>
                No characters match the selected filters.
              </p>
            </Grid.Column>
          </Grid.Row>
        ) : (
          filteredCharacters.map((character) => {
            const oppData = getCharacterOpportunityData(character.uuid);
            const opportunities = oppData.opportunities || [];
            const oppSummary = getOpportunityStatusSummary(opportunities);
            const isSelected = selectedCharacters.has(character.uuid);

            return (
              <Grid.Row
                key={character.uuid}
                style={{
                  padding: '10px 0',
                  backgroundColor: characterId === character.uuid ? '#ebf3fb' : 'transparent',
                  cursor: 'pointer',
                }}
                onClick={() => handleCharacterSelect(character)}
                className={characterId === character.uuid ? 'bw-character-grid-container-row bw-selected' : 'bw-character-grid-container-row'}
              >
                {/* Import checkbox column */}

                <Grid.Column width={1} onClick={(e) => e.stopPropagation()}>
                  {showImportCheckboxes && (
                    <Checkbox
                      checked={isSelected}
                      onChange={(e, { checked }) => handleCharacterImportChange(character.uuid, checked)}
                    />
                  )}

                </Grid.Column>

                {/* Character info column */}
                <Grid.Column width={8}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%'
                  }}>
                    <div style={{ fontWeight: 'medium', display: 'flex', alignItems: 'center', gap: '0.5em' }}>
                      {character.name}
                      {/* Variations count with popup */}
                      {character.variationCount !== undefined && (
                        <Popup
                          trigger={
                            <span style={{ color: '#666', fontSize: '0.9em' }}>
                              ({character.variationCount})
                            </span>
                          }
                          content={`This character has ${character.variationCount} voice variations`}
                          size="small"
                        />
                      )}
                      {/* Assignment status indicator */}
                      {character.assignmentStatus === 'assigned' && (
                        <Label color="green" size="tiny">
                          <Icon name="user" />
                          Assigned
                        </Label>
                      )}
                      {/* Interest count indicator */}
                      {character.interestCount > 0 && (
                        <Label color="blue" size="tiny">
                          <Icon name="heart" />
                          {character.interestCount} interested
                        </Label>
                      )}
                    </div>
                    <StatusLabels
                      priority={mapPriorityForDisplay(character.priority) || 'Medium'}
                      status={mapStatusForDisplay(character.status) || 'New'}
                      deadline={character.deadline}
                      getPriorityColor={getPriorityColor}
                      getStatusColor={getStatusColor}
                    />
                  </div>
                </Grid.Column>

                {/* Opportunity status column */}
                {showOpportunityStatus && (
                  <Grid.Column width={showImportCheckboxes ? 4 : 4}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}>
                      <Label
                        color={oppSummary.color}
                        size="small"
                        style={{ margin: 0 }}
                      >
                        {oppSummary.text}
                      </Label>
                      {opportunities.length > 0 && (
                        <Button
                          as={Link}
                          to={`/opportunities/manage?character=${character.uuid}`}
                          state={{
                            from: location.pathname + location.search,
                            backButtonText: 'Back to Opportunity Management Hub'
                          }}
                          size="mini"
                          basic
                          icon="eye"
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                    </div>
                  </Grid.Column>
                )}

                {/* Actions column */}
                <Grid.Column width={showImportCheckboxes ? 3 : 3} textAlign="right" style={{ paddingLeft: "0px" }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.5em' }}>
                    {showOpportunityStatus && opportunities.length === 0 && (
                      <Popup
                        trigger={
                          <Icon
                            name="plus circle"
                            color="green"
                            size="large"
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCreateOpportunity(character);
                            }}
                            loading={loadingStates[`create-${character.uuid}`]}
                          />
                        }
                        content="Create opportunity for this character"
                        size="small"
                      />
                    )}
                    <Icon
                      name="trash alternate"
                      color="red"
                      className="bw-trash-icon"
                      style={{ cursor: 'pointer' }}
                      onClick={(e) => handleDeleteClick(e, character)}
                    />
                  </div>
                </Grid.Column>
              </Grid.Row>
            );
          })
        )}
      </Grid>
    </div>
  );
});

export default CharactersListEnh;
